/* eslint no-constant-condition: 0  */
import { call, put, takeLatest, select, all } from "redux-saga/effects";
import ApiService from "../../service/ApiService";
import RestService from "../../service/RestService";
import {
  LOGIN_REQUEST,
  LOGIN,
  LOGOUT_REQUEST,
  LOGOUT,
  LOGISTICCOMPANY_PREPARE_CHANGE,
  MANAGED_LOGISTICCOMPANIES,
  PLATFORM_SETTINGS
} from "./index";
import * as authAction from "./authAction";
import { restServiceMiddleware } from "./authUtils";

function* loadManagedLogisticCompanies({ token }) {
  const { managedLogisticcompanies: payload } = yield call(
    ApiService.getManagedLogisticCompanies,
    token
  );
  yield put({
    type: MANAGED_LOGISTICCOMPANIES,
    payload
  });
}

function* loadPlatformSettings({ token, isHome = false }) {
  const { settings: payload } = yield call(ApiService.getSettings, token);
  yield put({
    type: PLATFORM_SETTINGS,
    payload,
    isHome
  });
}

function setServiceMiddleware({ logisticCompanyId }) {
  RestService.registerMiddleware(
    "logisticCompany",
    restServiceMiddleware({ logisticCompanyId })
  );
}

function* login({ payload: { username, password } }) {
  try {
    // reset middleware
    setServiceMiddleware({ logisticCompanyId: 0 });

    const auth = yield call(ApiService.login, username, password);
    yield loadManagedLogisticCompanies(auth);
    // Beim Login werden die Settings des übergeordneten (Heimat-)Mandanten
    // erfasst – diese steuern dauerhaft die Sidebar-Sichtbarkeit.
    yield loadPlatformSettings({ ...auth, isHome: true });

    yield put({
      type: LOGIN,
      payload: auth
    });
  } catch (error) {
    yield put({
      type: LOGIN,
      payload: error,
      error: true
    });
  }
}

function* logout() {
  const token = yield select(state => state.auth.token);
  try {
    yield call(ApiService.logout, token);

    yield put({
      type: LOGOUT
    });
  } catch (error) {
    yield put({
      type: LOGOUT,
      payload: error,
      error: true
    });
  }
  // reset middleware
  setServiceMiddleware({ logisticCompanyId: 0 });
}

/**
 * Source: https://github.com/redux-saga/redux-saga/blob/master/docs/introduction/BeginnerTutorial.md
 * @param {number} ms
 */
const delay = ms => new Promise(res => setTimeout(res, ms));

/**
 * Change logisticCompanyId.
 * @param {object} params
 * @param {object} socket
 */
function* changeLogisticCompany({ logisticCompanyId }) {
  try {
    yield delay(400);
    setServiceMiddleware({ logisticCompanyId });
    const token = yield select(state => state.auth.token);
    if (token) {
      try {
        yield loadPlatformSettings({ token });
      } catch (error) {
        console.error("Failed to reload platform settings after tenant switch", error);
      }
    }
  } finally {
    yield put(authAction.logisticCompanyChanged());
  }
}

function* bootstrapHomePlatformSettings() {
  const { token, user, homePlatformSettings, platformSettings, logisticCompanyId } =
    yield select(state => state.auth);

  if (!token || !user || homePlatformSettings) {
    return;
  }

  const homeTenantId = user.logisticcompany_id;
  const activeTenantId = logisticCompanyId === 0 ? homeTenantId : logisticCompanyId;

  if (activeTenantId === homeTenantId && platformSettings) {
    yield put({
      type: PLATFORM_SETTINGS,
      payload: platformSettings,
      isHome: true
    });
    return;
  }

  const previousTenantId = logisticCompanyId;
  setServiceMiddleware({ logisticCompanyId: 0 });
  try {
    yield loadPlatformSettings({ token, isHome: true });
  } catch (error) {
    console.error("Failed to load home platform settings", error);
  } finally {
    setServiceMiddleware({ logisticCompanyId: previousTenantId });
  }
}

export default function* sagas(store) {
  yield all([
    takeLatest(LOGIN_REQUEST, login),
    takeLatest(LOGOUT_REQUEST, logout),
    takeLatest(LOGISTICCOMPANY_PREPARE_CHANGE, changeLogisticCompany)
  ]);
  yield call(bootstrapHomePlatformSettings);
}
